import React from "react";
import {
  Box,
  TextField,
  FormControlLabel,
  Checkbox,
  MenuItem,
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const LineupSlots = ({
  inputCounts,
  selectedBox,
  inputValues,
  handleInputValueChange,
  inputStyles,
}) => {
  if (selectedBox === null) return null;

  return (
    <Box mt={6}>
      {[...Array(Math.min(inputCounts[selectedBox] || 0, 9))].map((_, idx) => (
        <Box
          key={idx}
          sx={{
            display: "flex",
            gap: 6,
            alignItems: "flex-start",
            mb: 4,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 54,
              backgroundColor: "#1976d2",
              borderRadius: "10px",
              color: "white",
              fontWeight: "bold",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              userSelect: "none",
              flexShrink: 0,
              mt: 4,
            }}
          >
            {idx + 1}
          </Box>

          <Box
            sx={{
              maxWidth: "314px",
              flexShrink: 0,
              display: "flex",
              alignItems: "flex-start",
              flexDirection: "column",
              gap: 1,
            }}
          >
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#464A53",
                mb: 1,
                alignSelf: "center",
              }}
            >
              Card Series
            </Box>
            {(inputValues[`cardSeries-${idx}`] || [""]).map(
              (seriesValue, sIdx) => (
                <TextField
                  key={sIdx}
                  select
                  variant="outlined"
                  placeholder="NHL Card Series 2024/25"
                  value={seriesValue}
                  onChange={(e) => {
                    const updated = [
                      ...(inputValues[`cardSeries-${idx}`] || [""]),
                    ];
                    updated[sIdx] = e.target.value;
                    handleInputValueChange(`cardSeries-${idx}`, updated);
                  }}
                  size="medium"
                  SelectProps={{
                    IconComponent: KeyboardArrowDownIcon,
                    displayEmpty: true,
                    renderValue: (selected) => {
                      if (!selected) {
                        return (
                          <span style={{ color: "#9e9e9e" }}>
                            NHL Card Series 2024/25
                          </span>
                        );
                      }
                      return selected;
                    },
                  }}
                  sx={{
                    ...inputStyles,
                    borderRadius: "50px",
                    "& .MuiSelect-select": { paddingY: "10px" },
                  }}
                >
                  <MenuItem value="NHL Card Series 2023/24">
                    NHL Card Series 2023/24
                  </MenuItem>
                  <MenuItem value="NHL Card Series 2024/25">
                    NHL Card Series 2024/25
                  </MenuItem>
                </TextField>
              )
            )}
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#1976d2",
                cursor: "pointer",
              }}
              onClick={() => {
                const updated = [...(inputValues[`cardSeries-${idx}`] || [""])];
                updated.push("");
                handleInputValueChange(`cardSeries-${idx}`, updated);
              }}
            >
              + Add Card Series
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#464A53",
                mb: 1,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              Label
            </Box>
            <TextField
              variant="outlined"
              value={inputValues[`label-${idx}`] || ""}
              onChange={(e) =>
                handleInputValueChange(`label-${idx}`, e.target.value)
              }
              size="small"
              sx={{
                ...inputStyles,
                maxWidth: "143px",
                borderRadius: "50px",
              }}
            />
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#464A53",
                mb: 1,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              Position
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
              {["C", "LW", "RW", "D", "Team"].map((pos) => (
                <FormControlLabel
                  key={pos}
                  control={
                    <Checkbox
                      checked={
                        inputValues[`position-${idx}`]?.includes(pos) || false
                      }
                      onChange={(e) => {
                        const prevPositions =
                          inputValues[`position-${idx}`] || [];
                        let newPositions;
                        if (e.target.checked) {
                          newPositions = [...prevPositions, pos];
                        } else {
                          newPositions = prevPositions.filter((p) => p !== pos);
                        }
                        handleInputValueChange(`position-${idx}`, newPositions);

                        const currentLabel = inputValues[`label-${idx}`] || "";
                        const autoLabel = prevPositions.join(", ");
                        if (currentLabel === autoLabel || currentLabel === "") {
                          handleInputValueChange(
                            `label-${idx}`,
                            newPositions.join(", ")
                          );
                        }
                      }}
                      sx={{
                        color: "#6A707E",
                        "&.Mui-checked": { color: "#1976d2" },
                        padding: "4px",
                      }}
                    />
                  }
                  label={pos}
                  sx={{
                    marginRight: "8px",
                    ".MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#6A707E",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>

          <Box sx={{ display: "flex", flexDirection: "column" }}>
            <Box
              sx={{
                fontSize: "16px",
                fontWeight: 600,
                color: "#464A53",
                mb: 1,
                alignItems: "center",
                display: "flex",
                justifyContent: "center",
              }}
            >
              Grade
            </Box>
            <Box sx={{ display: "flex", gap: 2, flexShrink: 0 }}>
              {["All-Star", "Gold", "Silver", "Base"].map((grade) => (
                <FormControlLabel
                  key={grade}
                  control={
                    <Checkbox
                      checked={
                        inputValues[`grade-${idx}`]?.includes(grade) || false
                      }
                      onChange={(e) => {
                        const prevGrades = inputValues[`grade-${idx}`] || [];
                        let newGrades;
                        if (e.target.checked) {
                          newGrades = [...prevGrades, grade];
                        } else {
                          newGrades = prevGrades.filter((g) => g !== grade);
                        }
                        handleInputValueChange(`grade-${idx}`, newGrades);
                      }}
                      sx={{
                        color: "#6A707E",
                        "&.Mui-checked": { color: "#1976d2" },
                        padding: "4px",
                      }}
                    />
                  }
                  label={grade}
                  sx={{
                    marginRight: "8px",
                    ".MuiFormControlLabel-label": {
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "#6A707E",
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
};

export default LineupSlots;
